import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import DateRangePicker from '../components/DateRangePicker';
import KpiCard from '../components/KpiCard';
import { fmtMoney } from '../lib/fmt';
import { MoneyInput } from '../components/inputs/MoneyInput';
import { MonthMetaCard } from '../components/MonthMetaCard';
import { SEM_COLORS } from '../lib/colors';
import {
  isoDate, weekStartISO, addDaysISO, endOfDefaultProjection,
  resolveDefaultRangeEnd, computeWeekRangeLabel, computeMesLabel,
  computeSemanaDoMes, countMondaysInMonth, getDefaultMonths,
} from '../lib/dateHelpers';
import type {
  CronogramaConfigOut,
  CronogramaSemanalOut,
  CronogramaSemanaOut,
  CronogramaWeekOverrideOut,
  TitulosWeekOut,
  TransmiteWeekOut,
} from '../types/api';


const semColor = (n: number) => SEM_COLORS[n] ?? '#8A9BB8';
const EMPTY_TRANSMITE: TransmiteWeekOut[] = [];
const EMPTY_TITULOS: TitulosWeekOut[] = [];

// ── WeekCard ──────────────────────────────────────────────────────────────────

interface CardData {
  wk: string;
  ym: string;
  mesLabel: string;
  semanaDoMes: number;
  rangeLabel: string;
  totalPedidos: number;
  qtdParcelas: number;
  fornecedoresPO: string[];
  transmiteValor: number;
  capacidadeOverride: number;
  aPagarProjetado: number;
  aPagarRealizado: number;
  saidaEcommerceValor: number;
  saidaEcommerceQtd: number;
  saidaEcommerceNomes: string[];
  isCurrent: boolean;
}

interface WeekCardProps {
  card: CardData;
  metaVenda: number;
  taxaPct: number;
  onSave: (wk: string, cap: number | null, proj: number | null, real: number | null) => void;
}

function WeekCard({ card, metaVenda, taxaPct, onSave }: WeekCardProps) {
  const [localCap, setLocalCap] = useState<number>(card.capacidadeOverride > 0 ? card.capacidadeOverride : 0);

  useEffect(() => {
    setLocalCap(card.capacidadeOverride > 0 ? card.capacidadeOverride : 0);
  }, [card.capacidadeOverride]);

  function handleBlur() {
    // Projetado vem do upload de Títulos (read-only) — não é persistido como override.
    onSave(
      card.wk,
      localCap || null,
      null,
      null,
    );
  }

  // Capacidade: auto from meta, or override
  const capOverride = localCap || 0;
  const nSem = countMondaysInMonth(card.ym);
  const liquido = metaVenda > 0 ? metaVenda * (1 - taxaPct) : 0;
  const capacidadeAuto = liquido > 0 ? liquido / nSem : 0;
  const capacidade = capOverride > 0 ? capOverride : capacidadeAuto;
  const hasOverride = capOverride > 0;
  const hasMeta = metaVenda > 0;

  // Cascata: realizado > projetado (upload) > transmite
  const projetado = card.aPagarProjetado;
  const realizado = card.aPagarRealizado;
  let aPagarReal = 0;
  let fonte = '';
  if (realizado > 0) { aPagarReal = realizado; fonte = 'realizado'; }
  else if (projetado > 0) { aPagarReal = projetado; fonte = 'projetado'; }
  else if (card.transmiteValor > 0) { aPagarReal = card.transmiteValor; fonte = 'Transmite'; }

  const aPagarTotal = card.totalPedidos + aPagarReal;
  const saldo = capacidade > 0 ? capacidade - aPagarTotal : null;

  // Diff projetado × realizado
  const showDiff = projetado > 0 && realizado > 0;
  const diff = realizado - projetado;

  const fornPreview = card.fornecedoresPO.slice(0, 2).join(', ') + (card.fornecedoresPO.length > 2 ? '…' : '');
  const col = semColor(card.semanaDoMes);

  const [year, month] = card.ym.split('-').map(Number);
  const mesShort = new Date(year, month - 1, 1).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });

  return (
    <div
      className={`card p-4 ${card.isCurrent ? 'glow' : ''}`}
      style={{
        borderLeft: `4px solid ${col}`,
        background: card.isCurrent ? 'rgba(244,122,32,.04)' : undefined,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-mbc-soft text-sm font-bold capitalize">
            {card.mesLabel}
            {card.isCurrent && <span className="text-[9px] text-mbc-orangel uppercase ml-1">· atual</span>}
          </div>
          <div className="text-[10px] text-mbc-mute">{card.rangeLabel}</div>
        </div>
        <span className="badge" style={{ background: `${col}33`, color: col }}>
          Sem {card.semanaDoMes}
        </span>
      </div>

      {/* A pagar pedidos — destaque */}
      <div className="p-3 rounded-lg mb-2" style={{ background: 'rgba(30,111,214,.14)', border: '1px solid rgba(30,111,214,.35)' }}>
        <div className="text-[10px] text-mbc-bluel uppercase tracking-wider font-bold">📊 A pagar pedidos</div>
        <div className="text-mbc-bluel font-extrabold tabular-nums text-2xl mt-0.5">{fmtMoney(card.totalPedidos)}</div>
        <div className="text-[10px] text-mbc-mute">
          {card.qtdParcelas} parcela{card.qtdParcelas !== 1 ? 's' : ''} · pedidos aprovados
        </div>
      </div>

      {/* 3 colunas: Transmite, Projetado, Saída Ecommerce */}
      <div className="grid grid-cols-3 gap-2 text-xs mb-2">
        <div className="p-2 rounded-lg" style={{ background: 'rgba(34,160,107,.08)' }}>
          <div className="text-[9px] text-mbc-mute uppercase tracking-wider">📑 Transmite</div>
          <div className="text-emerald-300 font-bold tabular-nums text-[11px]">
            {card.transmiteValor > 0 ? fmtMoney(card.transmiteValor) : '—'}
          </div>
          <div className="text-[10px] text-mbc-mute">{card.transmiteValor > 0 ? 'planilha' : 'sem dado'}</div>
        </div>

        <div className="p-2 rounded-lg" style={{ background: 'rgba(244,122,32,.10)' }}>
          <div className="text-[9px] text-mbc-orangel uppercase tracking-wider font-bold">📥 Projet.</div>
          <div className="text-mbc-orangel font-bold tabular-nums text-[11px] mt-0.5">
            {card.aPagarProjetado > 0 ? fmtMoney(card.aPagarProjetado) : '—'}
          </div>
          <div className="text-[10px] text-mbc-mute">{card.aPagarProjetado > 0 ? 'do upload' : 'sem dado'}</div>
        </div>

        <div className="p-2 rounded-lg" style={{ background: 'rgba(167,139,250,.10)' }}>
          <div className="text-[9px] text-violet-300 uppercase tracking-wider font-bold">🛒 Saída Ecommerce</div>
          <div className="text-violet-300 font-bold tabular-nums text-[11px] mt-0.5">
            {card.saidaEcommerceValor > 0 ? fmtMoney(card.saidaEcommerceValor) : '—'}
          </div>
          <div
            className="text-[10px] text-mbc-mute truncate"
            title={card.saidaEcommerceNomes.join(', ')}
          >
            {card.saidaEcommerceQtd > 0
              ? `${card.saidaEcommerceQtd} título${card.saidaEcommerceQtd !== 1 ? 's' : ''} · ${card.saidaEcommerceNomes.join(', ')}`
              : 'sem Classif Forn'}
          </div>
        </div>
      </div>

      {/* Diff projetado × realizado */}
      {showDiff && (
        <div
          className={`text-[10px] px-2 py-1 rounded mb-2 ${diff > 0 ? 'text-rose-300' : 'text-emerald-300'}`}
          style={{ background: diff > 0 ? 'rgba(244,63,94,.08)' : 'rgba(34,160,107,.08)' }}
        >
          Δ Projet × Realiz:{' '}
          {diff > 0
            ? `+${fmtMoney(diff)} (gastou mais que projetado)`
            : diff < -500
            ? `−${fmtMoney(Math.abs(diff))} (economizou)`
            : '≈ bateu o projetado'}
        </div>
      )}

      {/* Capacidade — derivação visível */}
      <div className="p-3 rounded-lg mb-2" style={{ background: 'rgba(125,211,252,.08)', border: '1px solid rgba(125,211,252,.2)' }}>
        <div className="flex items-baseline justify-between mb-1.5">
          <div className="text-[9px] text-sky-300 uppercase tracking-wider font-bold">💼 Capacidade da semana</div>
          <div className="text-sky-300 font-extrabold tabular-nums text-base">
            {capacidade > 0 ? fmtMoney(capacidade) : '—'}
          </div>
        </div>
        {hasMeta ? (
          <div className="text-[10px] text-mbc-mute space-y-0.5">
            <div>📅 Meta {mesShort}: {fmtMoney(metaVenda)}</div>
            <div>📉 Líquido (−{(taxaPct * 100).toFixed(0)}% taxas): {fmtMoney(liquido)}</div>
            <div>÷ {nSem} semanas do mês = {fmtMoney(capacidadeAuto)}/sem</div>
          </div>
        ) : (
          <div className="text-[10px] text-amber-400">
            ⚠ Sem meta de {mesShort} preenchida. Volte no card 🎯 Meta no topo.
          </div>
        )}
        <div className="border-t border-mbc-line/50 mt-2 pt-2">
          <div className="text-[9px] text-mbc-mute mb-0.5">Override manual:</div>
          <MoneyInput
            value={localCap}
            onChange={setLocalCap}
            onBlur={handleBlur}
            placeholder="R$ 0,00 — usa cálculo auto"
            className="w-full bg-mbc-panel2 border border-mbc-line rounded px-2 py-1 text-xs text-white text-right"
          />
          {hasOverride && (
            <div className="text-[9px] text-mbc-orangel mt-1">⚠ Override ativo</div>
          )}
        </div>
      </div>

      {/* Saldo */}
      <div
        className="p-3 rounded-lg text-center"
        style={{
          background: saldo === null ? 'rgba(124,144,176,.08)' : saldo >= 0 ? 'rgba(34,160,107,.12)' : 'rgba(244,63,94,.12)',
        }}
      >
        <div className="text-[9px] uppercase tracking-wider text-mbc-mute">Saldo da semana</div>
        {saldo === null ? (
          <>
            <div className="text-mbc-mute font-extrabold text-xl">sem capacidade</div>
            <div className="text-[10px] text-mbc-mute mt-0.5">preencha a meta ou o override</div>
          </>
        ) : (
          <>
            <div className={`font-extrabold text-xl tabular-nums ${saldo >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
              {saldo >= 0 ? '+ ' : '− '}{fmtMoney(Math.abs(saldo))}
            </div>
            <div className="text-[10px] text-mbc-mute mt-0.5">
              pedidos {fmtMoney(card.totalPedidos)}
              {aPagarReal > 0 && ` + ${fonte} ${fmtMoney(aPagarReal)}`}
              {` = ${fmtMoney(aPagarTotal)} · cap ${fmtMoney(capacidade)}`}
            </div>
          </>
        )}
      </div>

      {/* Fornecedores rodapé */}
      {fornPreview && (
        <div className="text-[10px] text-mbc-mute mt-2 truncate" title={card.fornecedoresPO.join(', ')}>
          {fornPreview}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Cronograma() {
  const qc = useQueryClient();
  const [localTaxa, setLocalTaxa] = useState('25.0');
  const [todayIso, setTodayIso] = useState(() => isoDate(new Date()));
  const [dateFrom, setDateFrom] = useState(todayIso);
  const [dateTo, setDateTo] = useState(() => endOfDefaultProjection(todayIso));
  const [rangeTouched, setRangeTouched] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTodayIso(isoDate(new Date()));
    }, 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const { data: semanalData } = useQuery<CronogramaSemanalOut>({
    queryKey: ['cronograma-semanal', dateFrom, dateTo],
    queryFn: () =>
      api.get<CronogramaSemanalOut>('/pedidos/cronograma/semanal', {
        params: { date_from: dateFrom || undefined, date_to: dateTo || undefined },
      }).then(r => r.data),
  });
  const { data: semanalAvailableData } = useQuery<CronogramaSemanalOut>({
    queryKey: ['cronograma-semanal-disponivel'],
    queryFn: () => api.get<CronogramaSemanalOut>('/pedidos/cronograma/semanal').then(r => r.data),
  });
  const { data: sheetsConfig } = useQuery<CronogramaConfigOut>({
    queryKey: ['cronograma-config'],
    queryFn: () => api.get<CronogramaConfigOut>('/pedidos/cronograma/config').then(r => r.data),
  });
  const { data: transmiteQueryData } = useQuery<TransmiteWeekOut[]>({
    queryKey: ['cronograma-transmite'],
    queryFn: () => api.get<TransmiteWeekOut[]>('/pedidos/cronograma/transmite').then(r => r.data),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const transmiteData = transmiteQueryData ?? EMPTY_TRANSMITE;
  const { data: titulosQueryData } = useQuery<TitulosWeekOut[]>({
    queryKey: ['cronograma-titulos'],
    queryFn: () => api.get<TitulosWeekOut[]>('/pedidos/cronograma/titulos').then(r => r.data),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const titulosData = titulosQueryData ?? EMPTY_TITULOS;

  useEffect(() => {
    if (sheetsConfig?.taxa_pct) {
      setLocalTaxa((parseFloat(String(sheetsConfig.taxa_pct)) * 100).toFixed(1));
    }
  }, [sheetsConfig?.taxa_pct]);

  useEffect(() => {
    if (rangeTouched) return;
    const availableWeekStarts = Array.from(new Set([
      ...(semanalAvailableData?.semanas ?? []).map(s => s.week_start),
      ...transmiteData.map(s => s.week_start),
      ...titulosData.map(s => s.week_start),
    ]));
    setDateFrom(todayIso);
    setDateTo(resolveDefaultRangeEnd(todayIso, availableWeekStarts));
  }, [rangeTouched, semanalAvailableData?.semanas, todayIso, transmiteData, titulosData]);

  const saveConfigMut = useMutation({
    mutationFn: (taxa_pct: number) =>
      api.put<CronogramaConfigOut>('/pedidos/cronograma/config', { taxa_pct }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cronograma-config'] }),
  });
  const saveOverrideMut = useMutation({
    mutationFn: ({ weekStart, capacidade, proj, real }: { weekStart: string; capacidade: number | null; proj: number | null; real: number | null }) =>
      api.put(`/pedidos/cronograma/overrides/${weekStart}`, { capacidade, a_pagar_projetado: proj, a_pagar_realizado: real }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cronograma-semanal'] }),
  });
  const saveMetaMut = useMutation({
    mutationFn: ({ month, meta_venda }: { month: string; meta_venda: number | null }) =>
      api.put(`/pedidos/cronograma/metas/${month}`, { meta_venda }),
    onMutate: async ({ month, meta_venda }) => {
      await qc.cancelQueries({ queryKey: ['cronograma-semanal'] });
      const previous = qc.getQueriesData<CronogramaSemanalOut>({ queryKey: ['cronograma-semanal'] });
      qc.setQueriesData<CronogramaSemanalOut>({ queryKey: ['cronograma-semanal'] }, old => {
        if (!old) return old;
        return {
          ...old,
          month_metas: {
            ...old.month_metas,
            [month]: meta_venda,
          },
        };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.previous.forEach(([queryKey, data]) => {
        qc.setQueryData(queryKey, data);
      });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['cronograma-semanal'] }),
  });

  function handleTaxaBlur() {
    const v = parseFloat(localTaxa) / 100;
    if (!isFinite(v)) return;
    saveConfigMut.mutate(v);
  }

  const semanas = semanalData?.semanas ?? [];
  const kpis = semanalData?.kpis;
  const overrides = semanalData?.overrides ?? {};
  const monthMetas = semanalData?.month_metas ?? {};
  const taxaPct = parseFloat(String(sheetsConfig?.taxa_pct ?? '0.25')) || 0.25;
  const todayWk = weekStartISO(new Date(`${todayIso}T12:00:00`));
  const currentMonth = todayIso.slice(0, 7);
  const filteredTransmiteData = transmiteData.filter((week) => {
    const weekEnd = addDaysISO(week.week_start, 6);
    return (!dateFrom || weekEnd >= dateFrom) && (!dateTo || week.week_start <= dateTo);
  });
  const filteredTitulosData = titulosData.filter((week) => {
    const weekEnd = addDaysISO(week.week_start, 6);
    return (!dateFrom || weekEnd >= dateFrom) && (!dateTo || week.week_start <= dateTo);
  });

  const allWeekStarts = Array.from(new Set([
    ...semanas.map(s => s.week_start),
    ...filteredTransmiteData.map(s => s.week_start),
    ...filteredTitulosData.map(s => s.week_start),
  ])).sort();

  const allMonths = Array.from(new Set(allWeekStarts.map(wk => wk.slice(0, 7)))).sort();
  const displayMonths = allMonths.length > 0 ? allMonths : getDefaultMonths();
  const metaScrollRef = useRef<HTMLDivElement | null>(null);
  const metaDragRef = useRef({ active: false, startX: 0, scrollLeft: 0 });

  function handleMetaDragStart(event: React.PointerEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement;
    if (target.closest('input, button, select, textarea')) return;
    const el = metaScrollRef.current;
    if (!el) return;
    metaDragRef.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: el.scrollLeft,
    };
    el.setPointerCapture(event.pointerId);
  }

  function handleMetaDragMove(event: React.PointerEvent<HTMLDivElement>) {
    const el = metaScrollRef.current;
    if (!el || !metaDragRef.current.active) return;
    el.scrollLeft = metaDragRef.current.scrollLeft - (event.clientX - metaDragRef.current.startX);
  }

  function handleMetaDragEnd(event: React.PointerEvent<HTMLDivElement>) {
    const el = metaScrollRef.current;
    metaDragRef.current.active = false;
    if (el?.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
  }

  function buildCard(wk: string): { card: CardData; semana: CronogramaSemanaOut | undefined } {
    const sem = semanas.find(s => s.week_start === wk);
    const txWk = filteredTransmiteData.find(s => s.week_start === wk);
    const titWk = filteredTitulosData.find(s => s.week_start === wk);
    const ov: CronogramaWeekOverrideOut | undefined = overrides[wk];
    return {
      card: {
        wk,
        ym: wk.slice(0, 7),
        mesLabel: sem?.mes_label ?? computeMesLabel(wk),
        semanaDoMes: sem?.semana_do_mes ?? computeSemanaDoMes(wk),
        rangeLabel: sem?.range_label ?? computeWeekRangeLabel(wk),
        totalPedidos: sem?.total_pedidos ?? 0,
        qtdParcelas: sem?.qtd_parcelas ?? 0,
        fornecedoresPO: sem?.fornecedores ?? [],
        transmiteValor: txWk?.valor ?? 0,
        capacidadeOverride: ov ? parseFloat(String(ov.capacidade ?? '0')) || 0 : 0,
        aPagarProjetado: titWk?.valor ?? 0,
        aPagarRealizado: ov ? parseFloat(String(ov.a_pagar_realizado ?? '0')) || 0 : 0,
        saidaEcommerceValor: titWk?.valor_ecommerce ?? 0,
        saidaEcommerceQtd: titWk?.qtd_ecommerce ?? 0,
        saidaEcommerceNomes: titWk?.classif_forn ?? [],
        isCurrent: wk === todayWk,
      },
      semana: sem,
    };
  }

  const cards = allWeekStarts.map(wk => buildCard(wk).card);
  const hasData = semanas.length > 0 || transmiteData.length > 0 || titulosData.length > 0;

  const totalTransmiteValor = transmiteData.reduce((s, d) => s + d.valor, 0);
  const totalTransmiteFuturo = transmiteData.filter(d => d.week_start >= todayWk).reduce((s, d) => s + d.valor, 0);

  return (
    <div className="space-y-5">

      {/* Transmite banner */}
      {transmiteData.length > 0 && (
        <div className="card p-3" style={{ background: 'rgba(34,160,107,.08)', borderLeft: '3px solid #22a06b' }}>
          <div className="text-xs text-emerald-300">
            <strong>📦 Transmite (Notas Gerais) carregado:</strong>{' '}
            {transmiteData.length} semanas · total {fmtMoney(totalTransmiteValor)} ·{' '}
            a pagar desta semana em diante: <strong>{fmtMoney(totalTransmiteFuturo)}</strong>.
          </div>
        </div>
      )}

      {/* Header */}
      <div className="card p-5">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-white font-bold text-base flex items-center gap-2">🗓️ Cronograma de pagamentos</h3>
            <p className="text-xs text-mbc-mute mt-0.5">
              Apenas pedidos <span className="badge bg-emerald-500/20 text-emerald-300">Aprovado</span> geram parcelas.
              Cada card é uma semana.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <DateRangePicker
              from={dateFrom}
              to={dateTo}
              onChange={({ from, to }) => {
                setRangeTouched(true);
                setDateFrom(from);
                setDateTo(to);
              }}
              onClear={() => {
                setRangeTouched(false);
              }}
              label="Período"
              className="w-[380px] max-w-full"
            />
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-mbc-mute">Taxa</span>
              <input
                type="number" step="0.5" min="0" max="100"
                value={localTaxa}
                onChange={e => setLocalTaxa(e.target.value)}
                onBlur={handleTaxaBlur}
                className="w-16 bg-mbc-panel2 border border-mbc-line rounded px-2 py-1 text-xs text-white text-right"
              />
              <span className="text-[10px] text-mbc-mute">%</span>
            </div>
          </div>
        </div>

        {/* 🎯 Meta de venda × Capacidade automática */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-bold text-white">🎯 Meta de venda × Capacidade automática</div>
              <div className="text-[10px] text-mbc-mute">A capacidade de cada semana é auto-calculada: (Meta − Taxas) ÷ semanas do mês.</div>
            </div>
          </div>
          <div
            ref={metaScrollRef}
            onPointerDown={handleMetaDragStart}
            onPointerMove={handleMetaDragMove}
            onPointerUp={handleMetaDragEnd}
            onPointerCancel={handleMetaDragEnd}
            className="flex cursor-grab gap-3 overflow-x-auto pb-2 active:cursor-grabbing"
          >
            {displayMonths.map(ym => (
              <MonthMetaCard
                key={ym}
                ym={ym}
                metaVenda={monthMetas[ym] ?? 0}
                taxaPct={taxaPct}
                isCurrent={ym === currentMonth}
                onSave={(m, val) => saveMetaMut.mutate({ month: m, meta_venda: val })}
              />
            ))}
          </div>
        </div>

        {/* KPIs */}
        {kpis && (
          <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-5">
            <KpiCard label="Vencidos" value={fmtMoney(kpis.vencido)} sub="em atraso" accent="rose-300" icon="⚠️" />
            <KpiCard label="Próximos 7d" value={fmtMoney(kpis.prox_7d)} sub="a pagar" accent="amber-300" icon="📅" />
            <KpiCard label="Próximos 15d" value={fmtMoney(kpis.prox_15d)} sub="a pagar" accent="mbc-bluel" icon="📅" />
            <KpiCard label="Próximos 30d" value={fmtMoney(kpis.prox_30d)} sub="a pagar" accent="mbc-orangel" icon="📅" />
            <KpiCard label="Total programado" value={fmtMoney(kpis.total_geral)} sub={`${kpis.tot_parcelas} parcelas`} accent="sky-300" icon="💳" />
          </div>
        )}

        {/* Weekly cards grid */}
        {hasData ? (
          <>
            <div className="text-[11px] text-mbc-mute uppercase tracking-wider mb-3">
              Visão financeira por mês × semana do mês
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {cards.map(card => (
                <WeekCard
                  key={card.wk}
                  card={card}
                  metaVenda={monthMetas[card.ym] ?? 0}
                  taxaPct={taxaPct}
                  onSave={(wk, cap, proj, real) =>
                    saveOverrideMut.mutate({ weekStart: wk, capacidade: cap, proj, real })
                  }
                />
              ))}
            </div>
            <div className="text-[10px] text-mbc-mute mt-3">
              💡 <strong>Cascata do saldo</strong>:{' '}
              Realizado {'>'} Projetado {'>'} Transmite.
              <code className="ml-1">Saldo = Capacidade − (Pedidos + fonte mais precisa)</code>
            </div>
          </>
        ) : (
          <div className="text-center text-mbc-mute text-sm py-8">
            {kpis && kpis.tot_pedidos_aguardando > 0
              ? `⏳ ${kpis.tot_pedidos_aguardando} pedido${kpis.tot_pedidos_aguardando > 1 ? 's' : ''} aguardando aprovação no Financeiro.`
              : 'Nenhum pedido aprovado ainda. Importe e aprove pedidos no Financeiro.'}
          </div>
        )}
      </div>
    </div>
  );
}
